// /app/frontend/src/components/frugal-v2/pages/FrugalDashboardV2.jsx
// Main Frugal Dashboard v2 - Orchestration only (200 lines)

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, AuthProvider } from '../hooks/useAuth';
import useFrugalData from '../hooks/useFrugalData';
import { useVIPSupport } from '../hooks/useVIPSupport';
import useSubscription from '../hooks/useSubscription';
import { useZenLock } from '../hooks/useZenLock';
import useCashflowAlerts from '../hooks/useCashflowAlerts';

// Frugal V2 i18n provider (independent from monolith)
import FrugalI18nProvider from '../providers/I18nProvider';
import frugalI18n, { changeLanguage } from '../i18n';

// Components
import DashboardHeader from '../components/DashboardHeader';
import BottomNav, { TABS } from '../components/BottomNav';
import VIPSupportToast from '../components/VIPSupportToast';
import TrialBanner from '../components/TrialBanner';
import CloseStateBanner from '../components/CloseStateBanner';
import CloseMonthModal from '../components/CloseMonthModal';
import EmailsModal from '../components/EmailsModal';
import MonthEmailsModal from '../components/MonthEmailsModal';
import UnsavedChangesModal from '../components/UnsavedChangesModal';
import ZenLockScreen from '../components/ZenLockScreen';

// Tabs
import HomeTab from '../tabs/HomeTab';
import ExecutionTab from '../tabs/ExecutionTab';
import BudgetTab from '../tabs/BudgetTab';
import AccountsTab from '../tabs/AccountsTab';

// Inner component that uses Frugal's own i18n
const FrugalDashboardInner = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const token = localStorage.getItem('token');
  const [activeTab, setActiveTab] = useState(TABS.HOME);
  const [budgetHasChanges, setBudgetHasChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  
  // VIP Support (WhatsApp)
  const { showToast, dismissToast, handleToastAction } = useVIPSupport();
  
  // Zen Lock (security)
  const { isLocked, config: zenLockConfig, updateConfig: updateZenLock, verifyPin } = useZenLock(token);
  
  // Subscription status (full hook for banner)
  const subscription = useSubscription(token);
  const { daysRemaining, isTrialExpired, emailsEnabled, getPaymentLink } = subscription;

  // Cashflow alerts for the header bell
  const cashflowAlerts = useCashflowAlerts(token);
  
  const [showCloseMonthModal, setShowCloseMonthModal] = useState(false);
  const [showEmailsModal, setShowEmailsModal] = useState(false);
  const [showMonthEmailsModal, setShowMonthEmailsModal] = useState(false);

  const handleUpgrade = async (plan) => {
    const url = await getPaymentLink(plan);
    if (url) {
      window.location.href = url;
    }
  };

  // Función para cambiar de tab con verificación de cambios sin guardar
  const handleTabChange = (newTab) => {
    if (budgetHasChanges && activeTab === TABS.BUDGET && newTab !== TABS.BUDGET) {
      // Hay cambios sin guardar, mostrar modal
      setPendingTab(newTab);
      setShowUnsavedModal(true);
    } else {
      setActiveTab(newTab);
    }
  };

  const handleDiscardChanges = () => {
    setBudgetHasChanges(false);
    setShowUnsavedModal(false);
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowUnsavedModal(false);
    setPendingTab(null);
  };

  const {
    incomes,
    savings,
    reserves,
    expenses,
    transactions,
    vital_transactions,
    zen,
    alerts,
    contingency,
    pendingEmailsCount,
    loading,
    error,
    refresh,
    // Income actions
    addIncome,
    editIncome,
    toggleIncome,
    removeIncome,
    // Saving actions
    addSaving,
    editSaving,
    toggleSaving,
    removeSaving,
    // Expense actions
    addExpense,
    toggleExpense,
    editExpense,
    removeExpense,
    // Transaction actions
    addTransaction,
    editTransaction,
    removeTransaction
  } = useFrugalData(token);

  const handleLanguageChange = () => {
    const newLang = frugalI18n.language === 'es' ? 'en' : 'es';
    changeLanguage(newLang);
  };

  const handleCloseMonth = () => {
    setShowCloseMonthModal(true);
  };

  const handleCloseMonthSuccess = () => {
    // Refresh all data after closing the month
    refresh();
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-emerald-400 text-lg">{t('frugal.status.loading')}</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-900 flex items-center justify-center">
        <div className="text-red-400 text-center">
          <p>{t('frugal.status.error')}</p>
          <button onClick={refresh} className="mt-4 px-4 py-2 bg-emerald-500 text-white rounded-lg">
            {t('frugal.actions.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-900 overflow-x-hidden">
      {/* Zen Lock Screen */}
      {isLocked && (
        <ZenLockScreen onUnlock={verifyPin} t={t} />
      )}

      {/* Header */}
      <DashboardHeader
        user={user}
        onLogout={logout}
        onLanguageChange={handleLanguageChange}
        onCloseMonth={handleCloseMonth}
        currentLanguage={frugalI18n.language}
        alerts={cashflowAlerts}
        pendingEmails={pendingEmailsCount}
        onOpenEmails={() => setShowEmailsModal(true)}
        zenLockConfig={zenLockConfig}
        onUpdateZenLock={updateZenLock}
        t={t}
      />

      {/* Main Content - Scrollable container */}
      <main className="max-w-lg mx-auto px-4 pt-4 pb-24 min-h-[calc(100vh-120px)]">
        {/* Close-month grace period banner (only on day 1 of new month with prev unclosed) */}
        <div className="-mx-4 mb-4">
          <CloseStateBanner token={token} onClose={() => window.location.reload()} />
        </div>

        {/* Trial Countdown Banner (shows on warning + expired) */}
        <div className="-mx-4 mb-4">
          <TrialBanner subscription={subscription} token={token} t={t} />
        </div>

        {activeTab === TABS.HOME && (
          <HomeTab
            incomes={incomes}
            savings={savings}
            reserves={reserves}
            expenses={expenses}
            transactions={transactions}
            vital_transactions={vital_transactions}
            zen={zen}
            alerts={alerts}
            contingency={contingency}
            token={token}
            subscriptionStatus={user?.subscription_status}
            addIncome={addIncome}
            editIncome={editIncome}
            toggleIncome={toggleIncome}
            removeIncome={removeIncome}
            addSaving={addSaving}
            editSaving={editSaving}
            toggleSaving={toggleSaving}
            removeSaving={removeSaving}
            addExpense={addExpense}
            toggleExpense={toggleExpense}
            editExpense={editExpense}
            removeExpense={removeExpense}
            addTransaction={addTransaction}
            editTransaction={editTransaction}
            removeTransaction={removeTransaction}
            language={frugalI18n.language}
            onRefresh={refresh}
            t={t}
          />
        )}

        {activeTab === TABS.EXECUTION && (
          <ExecutionTab token={token} t={t} />
        )}

        {activeTab === TABS.BUDGET && (
          <BudgetTab 
            token={token} 
            t={t} 
            totalIncome={incomes.reduce((sum, inc) => sum + (inc.amount || 0), 0)}
            onHasChanges={setBudgetHasChanges}
            onRefresh={refresh}
          />
        )}

        {activeTab === TABS.ACCOUNTS && (
          <AccountsTab 
            token={token} 
            t={t} 
          />
        )}
      </main>

      {/* Emails Modal */}
      <EmailsModal
        isOpen={showEmailsModal}
        onClose={() => setShowEmailsModal(false)}
        token={token}
        expenses={expenses}
        incomes={incomes}
        onRefresh={refresh}
        emailsEnabled={emailsEnabled}
        onUpgrade={handleUpgrade}
        t={t}
      />

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        t={t}
      />

      {/* Modal de cambios sin guardar */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onStay={handleCancelNavigation}
        onDiscard={handleDiscardChanges}
        t={t}
      />
      <VIPSupportToast show={showToast} onDismiss={dismissToast} onAction={handleToastAction} mobile={true} t={t} />
      <CloseMonthModal isOpen={showCloseMonthModal} onClose={() => setShowCloseMonthModal(false)} token={token} t={t} onSuccess={handleCloseMonthSuccess} />
    </div>
  );
};

// Export wrapped with Frugal V2's own providers
const FrugalDashboardV2 = () => (
  <AuthProvider>
    <FrugalI18nProvider>
      <FrugalDashboardInner />
    </FrugalI18nProvider>
  </AuthProvider>
);

export default FrugalDashboardV2;
